import { useEffect, useState } from "react";
import { LEAD_SOURCES, LEAD_STATUSES } from "@/lib/leadConstants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";

const EMPTY = {
  full_name: "", mobile: "", email: "", city: "", state: "",
  source: "Website", status: "New Lead", interested_course: "", preferred_batch: "",
  expected_joining_month: "", budget: "", assigned_to: "", follow_up_date: "",
  next_follow_up_time: "", remarks: "",
};

// Module-level so its identity is stable across parent re-renders (prevents focus loss on typing).
function Field({ label, value, onChange, type = "text", required, placeholder, error, testid }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}{required && " *"}</Label>
      <Input type={type} value={value} onChange={onChange} placeholder={placeholder} data-testid={testid} />
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

export default function LeadFormDialog({ open, onOpenChange, lead, executives, onSave }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const isEdit = Boolean(lead?.id);

  useEffect(() => {
    if (open) {
      setForm(lead?.id ? { ...EMPTY, ...lead, budget: lead.budget ?? "" } : EMPTY);
      setErrors({});
    }
  }, [open, lead]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const field = (label, k, type = "text", required, placeholder) => (
    <Field
      label={label}
      value={form[k]}
      onChange={(e) => set(k, e.target.value)}
      type={type}
      required={required}
      placeholder={placeholder}
      error={errors[k]}
      testid={`lead-${k}-input`}
    />
  );

  const validate = () => {
    const e = {};
    if (!form.full_name || form.full_name.trim().length < 2) e.full_name = "Enter a valid name";
    if (!form.mobile || form.mobile.trim().length < 6) e.mobile = "Enter a valid mobile number";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = { ...form, budget: parseFloat(form.budget) || 0 };
      await onSave(payload, isEdit ? lead.id : null);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="lead-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit Lead ${lead.lead_code || ""}` : "New Lead"}</DialogTitle>
          <DialogDescription>Capture and manage prospect information.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {field("Full Name", "full_name", "text", true, "e.g. Ravi Kumar")}
            {field("Mobile Number", "mobile", "text", true, "10-digit mobile")}
            {field("Email Address", "email", "email", false, "name@email.com")}
            {field("City", "city")}
            {field("State", "state")}
            {field("Interested Course", "interested_course", "text", false, "e.g. Salesforce Admin")}
            {field("Preferred Batch", "preferred_batch", "text", false, "e.g. Weekend / Aug-2026")}
            {field("Expected Joining Month", "expected_joining_month", "month")}
            {field("Budget (₹)", "budget", "number", false, "e.g. 45000")}

            <div className="space-y-1.5">
              <Label>Lead Source</Label>
              <Select value={form.source} onValueChange={(v) => set("source", v)}>
                <SelectTrigger data-testid="lead-source-select"><SelectValue /></SelectTrigger>
                <SelectContent>{LEAD_SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Lead Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Assigned Sales Executive</Label>
              <Select value={form.assigned_to || "unassigned"} onValueChange={(v) => set("assigned_to", v === "unassigned" ? "" : v)}>
                <SelectTrigger data-testid="lead-assigned-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {executives.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {field("Follow-up Date", "follow_up_date", "date")}
            {field("Next Follow-up Time", "next_follow_up_time", "time")}
          </div>

          <div className="space-y-1.5">
            <Label>Remarks</Label>
            <Textarea value={form.remarks} onChange={(e) => set("remarks", e.target.value)} rows={3} data-testid="lead-remarks-input" />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-lead-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? "Save Changes" : "Create Lead"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
